import { useState, type ReactNode } from 'react'
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons'
import { faBuildingColumns, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Button, Dropdown, Icon, Loader, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import type { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { useReselector } from '@/utils/sharedHooks'
import { resolveInstitutionLogo } from '@/features/user/settings/sections/self-service/financial-institutions/AccountInstitutionLogo'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import type { ConnectedInstitution } from '@/features/user/settings/sections/self-service/financial-institutions/connectNewAccountMenu.helpers'
import {
  NO_NEW_ACCOUNTS_OUTCOME_PREFIX,
  useConnectAccounts,
  type ConnectedAccount,
} from '../../hooks/useConnectAccounts'
import { useAnimatedCount } from '../../hooks/useAnimatedCount'
import { plural } from '../../lib/plural'
import { initialsOf } from '@/components/MerchantLogo'
import { BUTTON_ROW, CardIcon, Shell } from './cardParts'
import type { WidgetProps } from './types'

/** The card's job, not the subject: one tile for every state. */
const Header = ({
  title,
  description,
  trailing,
}: {
  title: string
  description?: string
  trailing?: ReactNode
}) => (
  <div className="flex items-center gap-3">
    <CardIcon icon={faBuildingColumns} />

    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
        {title}
      </Text>
      {description && (
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {description}
        </Text>
      )}
    </div>

    {trailing}
  </div>
)

/** The bank's logo at 28 and full radius, else the institution's initials. */
const InstitutionMark = ({
  item,
  institution,
}: {
  item: PlaidItem | undefined
  institution: string
}) => {
  const [broken, setBroken] = useState(false)
  const src = resolveInstitutionLogo(item, institution)

  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setBroken(true)}
        className="size-7 shrink-0 rounded-full object-cover"
      />
    )
  }

  return (
    <div
      aria-hidden
      className="flex size-7 shrink-0 items-center justify-center rounded-full bg-neutral-50"
    >
      <Text variant={TypographyStyles.LABEL_XS} color={TextColors.MOSS}>
        {initialsOf(institution)}
      </Text>
    </div>
  )
}

/** Read by the model as the cue to show the add-an-account card. */
export const CANT_FIND_BANK_OUTCOME = "They can't find their bank to connect."

const CantFindBankButton = ({ onClick }: { onClick: () => void }) => (
  <Button variant="secondary" size="sm" onClick={onClick}>
    I can&apos;t find my bank
  </Button>
)

/** Version 1: nothing connected. The body carries the ask; there is no description. */
const ConnectPrompt = ({
  onConnect,
  onCantFindBank,
}: {
  onConnect: () => void
  onCantFindBank: () => void
}) => (
  <Shell>
    <Header title="Connect your bank accounts" />

    <Text
      variant={TypographyStyles.BODY_SM}
      className="max-w-[56ch] text-neutral-600"
    >
      Add every account the business touches, including personal ones
      you&apos;ve paid business costs from. Gaps here become gaps in your books.
    </Text>

    <div className={BUTTON_ROW}>
      <Button variant="primary" size="sm" onClick={onConnect}>
        Connect accounts
      </Button>
      <CantFindBankButton onClick={onCantFindBank} />
      <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
        Connect securely through Plaid.
      </Text>
    </div>
  </Shell>
)

const AccountRow = ({ account }: { account: ConnectedAccount }) => {
  const plaidItems = useReselector(selectPlaidItems)
  const transactionCount = useAnimatedCount(account.transactionCount)
  const item = account.plaidItemId ? plaidItems[account.plaidItemId] : undefined

  return (
    <li className="flex items-center gap-3 border-t border-neutral-50 py-3 first:border-t-0">
      <InstitutionMark item={item} institution={account.institution} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.HEADER}
          className="[overflow-wrap:anywhere]"
        >
          {account.name}
        </Text>
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {account.type}
          {account.mask ? ` •${account.mask}` : ''}
        </Text>
      </div>

      <Text
        variant={TypographyStyles.BODY_XS}
        color={TextColors.MUTED}
        className="shrink-0 tabular-nums"
      >
        {plural(transactionCount, 'transaction')}
      </Text>

      {account.pulled ? (
        <Icon
          icon={faCheck}
          color="text-green-100"
          className="shrink-0 text-[12px]"
          aria-hidden
        />
      ) : (
        <Loader
          inline
          size={12}
          className="shrink-0 text-neutral-400"
          role="status"
          aria-label="In progress"
        />
      )}
    </li>
  )
}

/**
 * Picking a bank already connected makes Plaid Link return a token the client
 * refuses, so adding an account there has to reopen that bank's own item. This
 * mirrors the settings page's Manage Connections menu.
 */
const AddBankMenu = ({
  institutions,
  onConnect,
  onManage,
}: {
  institutions: ConnectedInstitution[]
  onConnect: () => void
  onManage: (accountId: number) => void
}) => {
  if (institutions.length === 0) {
    return (
      <Button variant="secondary" size="sm" onClick={onConnect}>
        Add another bank
      </Button>
    )
  }

  return (
    <Dropdown>
      <Dropdown.Trigger asChild>
        <Button variant="secondary" size="sm">
          Add another bank
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Content align="start" className="text-xs">
        <Dropdown.Item className="text-xs" onSelect={onConnect}>
          Connect a new bank
        </Dropdown.Item>
        <Dropdown.Separator />
        {institutions.map((institution) => (
          <Dropdown.Item
            key={institution.plaidItemId}
            className="text-xs"
            disabled={institution.hasSyncError}
            onSelect={() => onManage(institution.accountId)}
          >
            {institution.name}
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown>
  )
}

/** Version 2: accounts connected, their transactions arriving. */
const ConnectedList = ({
  accounts,
  institutions,
  transactionCount,
  syncing,
  holding,
  onConnect,
  onManage,
  onContinue,
  onCantFindBank,
}: {
  accounts: ConnectedAccount[]
  institutions: ConnectedInstitution[]
  transactionCount: number
  syncing: boolean
  holding: boolean
  onConnect: () => void
  onManage: (accountId: number) => void
  onContinue: () => void
  onCantFindBank: () => void
}) => {
  const shownCount = useAnimatedCount(transactionCount)

  return (
    <Shell>
      <Header
        title={`${plural(accounts.length, 'account')} connected`}
        description={plural(shownCount, 'transaction')}
        trailing={
          syncing ? (
            <Loader
              inline
              size={16}
              className="shrink-0 text-neutral-400"
              role="status"
              aria-label="In progress"
            />
          ) : null
        }
      />

      <ul>
        {accounts.map((account) => (
          <AccountRow key={account.id} account={account} />
        ))}
      </ul>

      <div className={BUTTON_ROW}>
        <Button
          variant="primary"
          size="sm"
          loading={holding}
          onClick={onContinue}
        >
          Continue
        </Button>
        <AddBankMenu
          institutions={institutions}
          onConnect={onConnect}
          onManage={onManage}
        />
        <CantFindBankButton onClick={onCantFindBank} />
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          Anything missing? Add it before we read them.
        </Text>
      </div>
    </Shell>
  )
}

/** Version 3: the record row a settled card collapses to (6.1). */
const ConnectedRecord = ({ accounts }: { accounts: ConnectedAccount[] }) => (
  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[8px] border border-neutral-200 bg-neutral-25 px-3 py-2">
    <div className="flex shrink-0 items-center gap-2">
      <span aria-hidden className="flex w-4 shrink-0 justify-center">
        <Icon
          icon={faCircleCheck}
          color="text-neutral-400"
          className="text-[12px]"
        />
      </span>

      <Text
        variant={TypographyStyles.BODY_XS}
        color={TextColors.MUTED}
        className="whitespace-nowrap"
      >
        {plural(accounts.length, 'account')} connected
      </Text>
    </div>

    <Text
      variant={TypographyStyles.BODY_XS}
      color={TextColors.MUTED}
      className="ml-auto min-w-0 text-right tabular-nums [overflow-wrap:anywhere]"
    >
      {accounts
        .map((account) => (account.mask ? `•${account.mask}` : account.name))
        .join('  ')}
    </Text>
  </div>
)

export const ConnectFinancialAccountsWidget = ({
  resolved,
  outcome,
  toolCallId,
  onOutcome,
}: WidgetProps) => {
  const {
    accounts,
    institutions,
    transactionCount,
    syncing,
    openPlaid,
    manageBank,
    confirm,
    holding,
  } = useConnectAccounts(onOutcome, resolved, toolCallId)

  const cantFindBank = () =>
    onOutcome(CANT_FIND_BANK_OUTCOME, { label: "Couldn't find their bank" })

  if (resolved) {
    const recordedByHost =
      outcome === CANT_FIND_BANK_OUTCOME ||
      outcome?.startsWith(NO_NEW_ACCOUNTS_OUTCOME_PREFIX)
    return recordedByHost ? null : <ConnectedRecord accounts={accounts} />
  }

  if (accounts.length === 0) {
    return <ConnectPrompt onConnect={openPlaid} onCantFindBank={cantFindBank} />
  }

  return (
    <ConnectedList
      accounts={accounts}
      institutions={institutions}
      transactionCount={transactionCount}
      syncing={syncing}
      holding={holding}
      onConnect={openPlaid}
      onManage={manageBank}
      onContinue={confirm}
      onCantFindBank={cantFindBank}
    />
  )
}
